import React, { createContext, useContext, useState, useEffect } from 'react';

// Global timer state outside of React to persist across component unmounts
let globalTimerState = {
  isVisible: false,
  seconds: 300,
  title: "Complete booking in",
  reservationId: null,
  expiresAt: null
};

let globalTimerInterval = null;
let globalTimerCallback = null;

// Load timer state from localStorage on initialization
const loadTimerStateFromStorage = () => {
  try {
    const saved = localStorage.getItem('globalTimerState');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Check if timer should still be running based on expiration time
      if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
        const remainingSeconds = Math.max(0, Math.floor((new Date(parsed.expiresAt) - new Date()) / 1000));
        return {
          ...parsed,
          seconds: remainingSeconds,
          isVisible: remainingSeconds > 0
        };
      } else {
        // Timer expired, clear storage
        localStorage.removeItem('globalTimerState');
        return globalTimerState;
      }
    }
  } catch (error) {
    console.error('Error loading timer state from localStorage:', error);
  }
  return globalTimerState;
};

// Save timer state to localStorage
const saveTimerStateToStorage = (state) => {
  try {
    if (state.isVisible && state.expiresAt) {
      localStorage.setItem('globalTimerState', JSON.stringify(state));
    } else {
      localStorage.removeItem('globalTimerState');
    }
  } catch (error) {
    console.error('Error saving timer state to localStorage:', error);
  }
};

// Initialize with saved state
globalTimerState = loadTimerStateFromStorage();

const TimerContext = createContext();

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const [timerState, setTimerState] = useState(globalTimerState);

  const startGlobalTimer = (title = "Complete booking in", callback = null, reservationId = null, expiresAt = null) => {
    console.log('▶️ [TIMER] startGlobalTimer called', { title, callback: !!callback, reservationId, expiresAt });
    
    // Clear any existing timer and reset state
    if (globalTimerInterval) {
      clearInterval(globalTimerInterval);
      globalTimerInterval = null;
    }
    
    // Clear any existing callback
    globalTimerCallback = null;
    
    // Set the new callback
    globalTimerCallback = callback;
    
    // Calculate expiration time
    const expirationTime = expiresAt ? new Date(expiresAt) : new Date(Date.now() + 300 * 1000);
    
    // Reset timer state completely
    globalTimerState = {
      isVisible: true,
      seconds: 300,
      title,
      reservationId,
      expiresAt: expirationTime.toISOString()
    };
    
    console.log('▶️ [TIMER] Setting timer state to visible', globalTimerState);
    setTimerState({ ...globalTimerState });
    saveTimerStateToStorage(globalTimerState);
    
    // Debug: Check if state was set correctly
    setTimeout(() => {
      console.log('▶️ [TIMER] State after 100ms:', globalTimerState);
    }, 100);
    
    // Start countdown
    console.log('▶️ [TIMER] Starting countdown interval');
    globalTimerInterval = setInterval(() => {
      // Calculate remaining time based on expiration time for accuracy
      const now = new Date();
      const remainingSeconds = Math.max(0, Math.floor((expirationTime - now) / 1000));
      
      globalTimerState.seconds = remainingSeconds;
      console.log('⏰ [TIMER] Countdown tick:', globalTimerState.seconds, 'isVisible:', globalTimerState.isVisible);
      
      setTimerState({ ...globalTimerState });
      saveTimerStateToStorage(globalTimerState);
      
      if (remainingSeconds <= 0) {
        // Timer expired
        console.log('⏰ [TIMER] Timer expired, cleaning up');
        if (globalTimerInterval) {
          clearInterval(globalTimerInterval);
          globalTimerInterval = null;
        }
        globalTimerState.isVisible = false;
        globalTimerState.seconds = 300;
        globalTimerState.reservationId = null;
        globalTimerState.expiresAt = null;
        
        setTimerState({ ...globalTimerState });
        saveTimerStateToStorage(globalTimerState);
        
        // Execute callback if provided
        if (globalTimerCallback) {
          globalTimerCallback();
          globalTimerCallback = null;
        }
      }
    }, 1000);
  };

  const stopGlobalTimer = () => {
    console.log('🛑 [TIMER] stopGlobalTimer called');
    console.log('🛑 [TIMER] Stack trace:', new Error().stack);
    console.log('🛑 [TIMER] Current timer state before stopping:', globalTimerState);
    
    if (globalTimerInterval) {
      clearInterval(globalTimerInterval);
      globalTimerInterval = null;
    }
    globalTimerState.isVisible = false;
    globalTimerState.seconds = 300;
    globalTimerState.reservationId = null;
    globalTimerState.expiresAt = null;
    globalTimerCallback = null;
    setTimerState({ ...globalTimerState });
    saveTimerStateToStorage(globalTimerState);
    
    console.log('🛑 [TIMER] Timer state after stopping:', globalTimerState);
  };

  const resetGlobalTimer = () => {
    console.log('🔄 [TIMER] resetGlobalTimer called', new Error().stack);
    stopGlobalTimer();
    
    // Ensure complete reset
    globalTimerState = {
      isVisible: false,
      seconds: 300,
      title: "Complete booking in",
      reservationId: null,
      expiresAt: null
    };
    globalTimerInterval = null;
    globalTimerCallback = null;
    
    setTimerState({ ...globalTimerState });
    saveTimerStateToStorage(globalTimerState);
  };

  const getGlobalTimer = () => {
    return globalTimerState;
  };

  // Sync with global state on mount and restore timer if needed
  useEffect(() => {
    console.log('🔄 [TIMER] TimerProvider mounted, syncing state:', globalTimerState);
    console.log('🔄 [TIMER] TimerProvider mounted, localStorage state:', localStorage.getItem('globalTimerState'));
    
    // If timer should be running, start it
    if (globalTimerState.isVisible && globalTimerState.expiresAt) {
      const expirationTime = new Date(globalTimerState.expiresAt);
      const now = new Date();
      
      console.log('🔄 [TIMER] Checking timer expiration:', {
        expirationTime: expirationTime.toISOString(),
        now: now.toISOString(),
        isExpired: expirationTime <= now
      });
      
      if (expirationTime > now) {
        console.log('🔄 [TIMER] Restoring timer from localStorage');
        const remainingSeconds = Math.floor((expirationTime - now) / 1000);
        
        // Start the timer with the remaining time
        startGlobalTimer(
          globalTimerState.title,
          globalTimerCallback,
          globalTimerState.reservationId,
          globalTimerState.expiresAt
        );
      } else {
        console.log('🔄 [TIMER] Timer expired, clearing state');
        resetGlobalTimer();
      }
    } else {
      console.log('🔄 [TIMER] No timer to restore, setting current state');
      setTimerState({ ...globalTimerState });
    }
  }, []);

  // Debug timer state changes
  useEffect(() => {
    console.log('🔄 [TIMER] Timer state updated:', timerState);
    console.log('🔄 [TIMER] Timer isVisible:', timerState.isVisible);
    console.log('🔄 [TIMER] Timer seconds:', timerState.seconds);
  }, [timerState]);

  const value = {
    timerState,
    startGlobalTimer,
    stopGlobalTimer,
    resetGlobalTimer,
    getGlobalTimer
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
}; 